import { useDeleteDocument, useDocumentNames, useDownloadDocument } from "@api/documents";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import { Download } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridValidRowModel } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

const DocumentsGrid = observer(() => {
  const containerRef = useRef(null);
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const allDocumentNames = useDocumentNames();
  const { height } = useResizeObserver({ ref: containerRef });
  const [filename, setFilename] = useState("");
  const mutateDownload = useDownloadDocument(filename);
  const mutateDelete = useDeleteDocument();

  const handleFileSelect = async (params: any) => {
    setFilename(params.row.value);
    await mutateDownload.mutateAsync(params.row.value)?.then((res) => {
      handleDownloadDocument(res.fileName, res.data);
    });
  };

  const handelFileDelete = async (params: any) => {
    await mutateDelete.mutateAsync(params.row.value)?.then(() => {
      allDocumentNames.refetch();
    });
  };

  const handleDownloadDocument = (fileName: any, fileData: any) => {
    const blob = new Blob([fileData], { type: "application/octet-stream" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = createDownloadLink(downloadUrl, fileName);
    triggerDownload(link);
    setFilename("");
  };

  const createDownloadLink = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    return link;
  };

  const triggerDownload = (link: HTMLAnchorElement) => {
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const newRows: GridValidRowModel[] = (allDocumentNames.data || []).map((doc, index) => ({
      id: index,
      value: doc.fileName,
    }));
    setRows(newRows);
  }, [allDocumentNames.data]);

  const columns: GridColDef[] = [
    {
      field: "value",
      headerName: i18n.t("document-grid.filename"),
      editable: false,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("document-grid.action"),
      resizable: false,
      width: 90,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={`${params.id}`}
            icon={
              <Tooltip title={i18n.t("document-grid.columns.delete-icon.tooltip")}>
                <GridDeleteIcon fontSize="small" />
              </Tooltip>
            }
            label={i18n.t("document-grid.columns.delete-icon.label")}
            onClick={() => handelFileDelete(params)}
          />,
          <GridActionsCellItem
            key={`${params.id}`}
            icon={
              <Tooltip title={i18n.t("document-grid.columns.download-icon.tooltip")}>
                <Download fontSize="small" />
              </Tooltip>
            }
            label={i18n.t("document-grid.columns.download-icon.label")}
            onClick={() => handleFileSelect(params)}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={allDocumentNames.isPending}
          columns={columns}
          rows={rows}
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [{ field: "value", sort: "asc" }],
            },
          }}
          sx={{ height: `${(height || 400) - 16}px` }}
        />
      </Box>
    </>
  );
});

export default DocumentsGrid;
